import React, { useState, useEffect } from 'react';

// Context
import { useTemplateContext } from 'Context/AppContext';
import { useCedulasContext } from "Context/CedulasPageContext";

// Utils
import { isMobile } from 'Utils/constants';
import LightTooltip from 'Utils/LightTooltip.jsx'
import { useGlobalContext } from "Context/GlobalContext.js"; 

// Assets
import { SEARCH_ICON } from '../../../assets/index.js';
import styles from './styles.js';

// Components
import MissingPersonCard from '../../moleculas/MissingPersonCard/MissingPersonCard.js';
import AlertSmallTop from 'Components/moleculas/AlertSmallTop';
import PaginationFinder from './PaginationFinder.jsx';
//import MemoStateFilter from './StateFilter.jsx';
import MemoMunicipalityFilter from './MunicipalityFilter.jsx';
import CloseIconSelect from 'Utils/CloseIconSelect.jsx';
import SkeletonCedulas from './SkeletonCedulas.jsx';
import MemoEstatusFilter from './estatusFilter.jsx';

// MUI
import { styled } from '@mui/system';
import { DatePicker } from '@mui/x-date-pickers';
import { 
    Box, FormControl, IconButton, Container,
    InputLabel, List, ListItem, ListItemText, MenuItem, Select, 
    TextField, Typography, Grid, CircularProgress, Fade
} from '@mui/material';

import dayjs from 'dayjs';

const PersonsFinder = ({ onPressReport }) => {
    const { isSmallScreen } = useGlobalContext()
    const { typesOfSex } = useTemplateContext()
    const { 
        cedulasData, cedulas, handleStateMunicipalityWithCedulas, stateMunicipalityWithCedulas,
        handleFilterChange, searchPeopleCedulas, nameFilterRef, handleLoadingCedulas,
    } = useCedulasContext();
    
    const PickerContainer = styled(FormControl)(() => (styles.formControl));
    const [didPerformSearch, setDidPerformSearch] = useState(false);
    const [alertOpen, setAlertOpen] = useState(false);
    const [ageError, setAgeError] = useState(false);
    const [errorText, setErrorText] = useState('');

    useEffect(() => {
        const dataIsLoaded = stateMunicipalityWithCedulas && Object.keys(stateMunicipalityWithCedulas).length > 0;

        if (!dataIsLoaded) {
            handleStateMunicipalityWithCedulas();
        }

        handleLoadingCedulas('loadingState');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const LegendToTakeIntoAccount = () => {
        return (
            <Container maxWidth="lg">
                <Box sx={styles.legendContainer} paddingTop={10}>
                    <Typography sx={{ ...styles.redTitle, textAlign: 'left' }}>Ten en cuenta que…</Typography>
                    <List>
                        <ListItem>
                            <div style={styles.listItemCircleStyle}></div>
                            <ListItemText
                                primary={
                                    <Typography sx={styles.listText}>
                                        Sólo se publican las cédulas de personas cuyas{' '}
                                        <span style={styles.boldTextStyle}>familias autorizan que se haga pública la información</span>
                                    </Typography>
                                }
                            />
                        </ListItem>
                        <ListItem>
                            <div style={styles.listItemCircleStyle}></div>
                            <ListItemText
                                primary={
                                    <Typography variant="body1" sx={styles.listText}>
                                        Una familia puede <span style={styles.boldTextStyle}>solicitar que se suspenda la difusión de la cédula de su familiar en cualquier momento</span>
                                    </Typography>
                                }
                            />
                        </ListItem>
                        {
                            (didPerformSearch === false || (cedulas && cedulas.length > 0)) &&
                            <ListItem>
                                <div style={styles.listItemCircleStyle}></div>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={styles.listText}>
                                            Las cédulas emitidas en este sitio <span style={styles.boldTextStyle}>cuentan con un código QR para verificar su validez.</span>
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        }
                    </List>
                </Box>
            </Container>
        )
    }

    const copyURLToClipboard = (id_cedula_busqueda) => {
        const personURL = `${window.location.protocol}//${window.location.host}/cedulas-de-busqueda?id_cedula_busqueda=${id_cedula_busqueda}&share=true`;
        navigator.clipboard.writeText(personURL).then(function () {
            setAlertOpen(true)
        }).catch(function (error) {
            console.error('Error al copiar la URL: ' + error);
        });
    };

    const handleSearch = () => {
        setDidPerformSearch(true);
        searchPeopleCedulas(1);
    }
    
    return (
        <>
            <AlertSmallTop 
                type={'ok'}
                open={alertOpen}
                title={'Copiado en el portapapeles'}
                onClose={() => setAlertOpen(false)}
            />
            <Box id={'container'} sx={styles.container}>
                {!isMobile && <Typography sx={styles.redTitle}>Utiliza los filtros para encontrar la cédula de la persona que buscas</Typography>}
                <Box sx={styles.box}>
                    <Grid container spacing={0}>
                        <Grid item xs={12} md={11} sx={{ py: 1 }}>
                            <Grid container spacing={0}>
                                <Grid item xs={12} md={4} sx={{ p: 1 }}>
                                    <MemoEstatusFilter />
                                </Grid>
                                {/* <Grid item xs={12} md={4} sx={{ p: 1 }}>
                                    <MemoStateFilter />
                                </Grid> */}
                                <Grid item xs={12} md={4} sx={{ p: 1 }}>
                                    <MemoMunicipalityFilter />
                                </Grid>
                                <Grid item xs={12} md={2} sx={{ p: 1 }}>
                                    <LightTooltip 
                                        title="
                                            Si no conoces la fecha exacta de desaparición, busca por rango: indica desde qué fecha y hasta qué fecha quieres buscar. 
                                            Si conoces la fecha exacta, ingrésala en cualquiera de los dos campos y deja el otro vacío.
                                            " 
                                        placement={isSmallScreen ? "top" : "right"} 
                                        TransitionComponent={Fade}
                                        TransitionProps={{ timeout: 600 }}
                                        arrow
                                    >            
                                        <FormControl sx={{ width: '100%' }}>
                                            <DatePicker
                                                label="Fecha de desaparición inicial" 
                                                format="YYYY-MM-DD"
                                                value={
                                                    cedulasData.disappearanceDateStart
                                                        ? dayjs(cedulasData.disappearanceDateStart)
                                                        : null
                                                }
                                                onChange={handleFilterChange('disappearanceDateStart')}
                                                slotProps={{ 
                                                    textField: { 
                                                        size: 'small',
                                                    }, 
                                                    actionBar: { actions: ["clear"] }, 
                                                }}
                                                sx={styles.formControl}
                                            />
                                        </FormControl>
                                    </LightTooltip>
                                </Grid>
                                <Grid item xs={12} md={2} sx={{ p: 1 }}>
                                    <LightTooltip 
                                        title="
                                            Si no conoces la fecha exacta de desaparición, busca por rango: indica desde qué fecha y hasta qué fecha quieres buscar. 
                                            Si conoces la fecha exacta, ingrésala en cualquiera de los dos campos y deja el otro vacío.
                                            " 
                                        placement={isSmallScreen ? "top" : "right"} 
                                        TransitionComponent={Fade}
                                        TransitionProps={{ timeout: 600 }}
                                        arrow
                                    >                  
                                        <FormControl sx={{ width: '100%' }}>
                                            <DatePicker
                                                label="Fecha de desaparición final" 
                                                format="YYYY-MM-DD"
                                                value={
                                                    cedulasData.disappearanceDateEnd
                                                        ? dayjs(cedulasData.disappearanceDateEnd)
                                                        : null
                                                }
                                                onChange={handleFilterChange('disappearanceDateEnd')}
                                                minDate={
                                                    cedulasData.disappearanceDateStart
                                                        ? dayjs(cedulasData.disappearanceDateStart)
                                                        : undefined
                                                }
                                                slotProps={{ 
                                                    textField: { 
                                                        size: 'small',
                                                    }, 
                                                    actionBar: { actions: ["clear"] }, 
                                                }}
                                                sx={styles.formControl}
                                            />
                                        </FormControl>
                                    </LightTooltip>
                                </Grid>
                                {/* <Grid item xs={12} md={2} sx={{ p: 1 }}>
                                    <FormControl sx={{ width: '100%' }}>
                                        <DatePicker
                                            label="Fecha de creación" 
                                            format="YYYY-MM-DD"
                                            size='small'
                                            value={cedulasData.creationDate}
                                            open={openCalendarCreation}
                                            onAccept={handleCloseCalendarCreation}
                                            onClose={handleCloseCalendarCreation}
                                            onChange={handleFilterChange('creationDate')}
                                            slotProps={{ 
                                                textField: { 
                                                    size: 'small',
                                                    onClick: handleOpenCalendarCreation,
                                                    InputProps: { endAdornment: null },
                                                }, 
                                                actionBar: { actions: ["clear"] }, 
                                            }}
                                            sx={styles.formControl}
                                        />
                                    </FormControl>
                                </Grid> */}
                                <Grid item xs={12} md={4} sx={{ p: 1 }}>
                                    <PickerContainer size="small" sx={{ width: '100%' }}>
                                        <InputLabel id="demo-select-small-label">Sexo</InputLabel>
                                        <Select
                                            labelId="demo-select-small-label"
                                            id="demo-select-small"
                                            value={cedulasData.sex}
                                            label="Sexo"
                                            onChange={handleFilterChange('sex')}
                                            endAdornment={<CloseIconSelect dataCondition={cedulasData.sex} dataFuntion={handleFilterChange('sex')}/>}
                                        >
                                            {typesOfSex && typesOfSex.map((option, index) => (
                                                <MenuItem key={`sexo_finder_${index}`} value={option.id}>
                                                    {option.descripcion}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </PickerContainer>
                                </Grid>
                                <Grid item xs={12} md={4} sx={{ p: 1 }}>
                                    <FormControl sx={{ width: '100%' }}>
                                        <TextField
                                            error={ageError}
                                            type="number"
                                            label="Edad al momento de desaparición"
                                            InputProps={{ inputProps: {min: 0, max: 125} }}
                                            value={cedulasData.age}
                                            helperText={errorText}
                                            onChange={(event) => {
                                                event.target.value = Math.max(0, parseInt(event.target.value) ).toString().slice(0,3);
                                                handleFilterChange('age')(event);
                                                if (parseInt(event.target.value) > 125) {
                                                    setAgeError(true);
                                                    setErrorText('La edad debe ser menor a 125')
                                                } else {
                                                    setAgeError(false);
                                                    setErrorText('')
                                                }
                                            }}
                                            size="small"
                                            sx={styles.formControl}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={4} sx={{ p: 1 }}>
                                    <LightTooltip 
                                        title="
                                            La búsqueda es por coincidencia exacta. Escribe todos los nombres y, de ser posible, también los apellidos.
                                            " 
                                        placement={isSmallScreen ? "top" : "right"} 
                                        TransitionComponent={Fade}
                                        TransitionProps={{ timeout: 600 }}
                                        arrow
                                    >            
                                    <FormControl sx={{ width: '100%' }}>
                                        <TextField
                                            inputRef={nameFilterRef}
                                            size="small"
                                            sx={styles.formControl}
                                            label="Nombres y/o Apellidos"
                                        />
                                    </FormControl>
                                    </LightTooltip>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} md={1} sx={{ p: 1, display: 'flex', alignItems: 'flex-end', justifyContent: { xs: 'center', md: 'flex-start' } }} >
                            <IconButton 
                                sx={{
                                    ...styles.button,
                                    width: { xs: '100%', md: 'auto' }
                                }} 
                                onClick={() => {
                                    handleSearch()
                                }}
                            >
                            {cedulasData?.loadingSearch ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                <>
                                {!isMobile && <img src={SEARCH_ICON} width={20} alt={'Icono de busqueda'}/>}
                                <Typography
                                    variant={'h6'}
                                    sx={{
                                    color: '#FFFFFF',
                                    fontFamily: 'Geologica Bold !important',
                                    ml: 1,
                                    display: { xs: 'block', md: 'none' },
                                    }}
                                >
                                    BUSCAR
                                </Typography>
                                </>
                            )}
                            </IconButton>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
            
            <Box sx={{ height: 'auto', overflowY: 'auto', width: '100%', marginTop: '-70px' }}>
                {cedulas && (
                    <>
                        <PaginationFinder />
                        <Grid container spacing={0}>
                            {cedulasData?.loadingSearch 
                                ?   <SkeletonCedulas />
                                :   cedulas.filter(i => i.autorizacion_informacion_publica === "SI").map((personItem, index) => (
                                        <Grid item key={`cedula_finder_${index}`} xs={12} md={6} lg={4} xl={4} sx={{ p: 1 }}>
                                            <MissingPersonCard  
                                                personItem={personItem} 
                                                onPressReport={onPressReport} 
                                                onPressShare={copyURLToClipboard}
                                            />
                                        </Grid>
                                    ))
                            }
                        </Grid>
                        <PaginationFinder />
                    </>
                )}
            </Box>
            {
                (cedulas && cedulas.length === 0) && 
                <Box sx={styles.notFoundAlert}>
                    <Typography variant={'h6'}>Lo sentimos, no encontramos ninguna cédula que cumpla con los criterios especificados.</Typography>
                </Box>
            }
            <LegendToTakeIntoAccount />
        </>
    );
};

export default PersonsFinder;
